/**
 * The sentence on a document card that says the document was generated from an
 * earlier data dictionary than the one the study has since adopted
 * (design 2026-09-14 §5).
 *
 * Rule: a document is out of date when its effective generation time - the
 * LATER of generatedAt and lastModifiedAt - is earlier than the adoption's
 * adoptedAt. No adoption, or no document, means no notice. A document that
 * exists but carries neither stamp gets the "unknown" sentence: nothing shows
 * it was made from the adopted dictionary.
 *
 * Dates are in the viewer's time zone (or options.timeZone), because the notice
 * sits beside the card's "Generated {toLocaleString()}" line and must agree with
 * it. When the adoption and the generation fall on the same calendar day in
 * that zone, the sentence adds both times, or it would read "changed on 15 Sep;
 * generated on 15 Sep" and look like a mistake.
 *
 * Pure - `web` has no test runner of its own; see dictionaryChangedNotice.test.ts.
 */

export interface DictionaryChangedNotice {
  kind: 'stale' | 'unknown';
  text: string;
}

export interface GeneratedDocStamps {
  generatedAt?: unknown;
  lastModifiedAt?: unknown;
}

export interface DictionaryChangedNoticeOptions {
  /** IANA zone, e.g. 'UTC' or 'America/New_York'. Default: the browser's zone. */
  timeZone?: string;
  /**
   * The document also carries a rename notice (features/renames). That notice
   * offers Regenerate, Update field names only and (for a backstop) Dismiss,
   * so this sentence points to it rather than telling the person to regenerate.
   */
  renameNoticeOpen?: boolean;
}

/**
 * The month names are ours, not Intl's: en-GB's short month for September is
 * "Sept" in current ICU data (Node 22 / ICU 76, and current Chrome), and the
 * agreed sentence says "15 Sep".
 */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Milliseconds from any shape a time arrives in: a Firestore Timestamp
 * (toMillis), anything with toDate, a plain { seconds, nanoseconds } object
 * (a Timestamp that lost its prototype), a callable-serialised
 * { _seconds, _nanoseconds }, a Date, or a number of milliseconds.
 * Anything unreadable is null, and counts as absent - including a finite number
 * outside the range a Date can hold (e.g. 1e20), which would make
 * Intl.DateTimeFormat.formatToParts throw "Invalid time value".
 */
const MAX_DATE_MS = 8.64e15;

function toMillis(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  let ms: unknown = null;
  if (typeof value === 'number') {
    ms = value;
  } else if (value instanceof Date) {
    ms = value.getTime();
  } else if (typeof value === 'object') {
    const v = value as {
      toMillis?: unknown;
      toDate?: unknown;
      seconds?: unknown;
      nanoseconds?: unknown;
      _seconds?: unknown;
      _nanoseconds?: unknown;
    };
    try {
      if (typeof v.toMillis === 'function') {
        ms = (v.toMillis as () => unknown).call(value);
      } else if (typeof v.toDate === 'function') {
        const d = (v.toDate as () => unknown).call(value);
        ms = d instanceof Date ? d.getTime() : null;
      } else if (typeof v.seconds === 'number') {
        const nanos = typeof v.nanoseconds === 'number' ? v.nanoseconds : 0;
        ms = v.seconds * 1000 + nanos / 1e6;
      } else if (typeof v._seconds === 'number') {
        const nanos = typeof v._nanoseconds === 'number' ? v._nanoseconds : 0;
        ms = v._seconds * 1000 + nanos / 1e6;
      }
    } catch {
      ms = null;
    }
  }
  return typeof ms === 'number' && Number.isFinite(ms) && Math.abs(ms) <= MAX_DATE_MS ? ms : null;
}

interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

function formatterFor(timeZone: string | undefined): Intl.DateTimeFormat {
  const opts: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone,
  };
  try {
    return new Intl.DateTimeFormat('en-GB', opts);
  } catch {
    // An unrecognised zone throws RangeError; a card must not crash over it.
    return new Intl.DateTimeFormat('en-GB', { ...opts, timeZone: undefined });
  }
}

/** The calendar date and wall-clock time of an instant in the formatter's zone. */
function zonedParts(ms: number, fmt: Intl.DateTimeFormat): ZonedParts {
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    Number(fmt.formatToParts(ms).find((p) => p.type === type)?.value);
  return { year: read('year'), month: read('month'), day: read('day'), hour: read('hour'), minute: read('minute') };
}

const sameDay = (a: ZonedParts, b: ZonedParts) => a.year === b.year && a.month === b.month && a.day === b.day;

/** "15 Sep", or "15 Sep at 09:00" when the time is wanted. */
function describe(p: ZonedParts, withTime: boolean): string {
  const day = `${p.day} ${MONTHS[p.month - 1]}`;
  if (!withTime) return day;
  const hh = String(p.hour % 24).padStart(2, '0');
  const mm = String(p.minute).padStart(2, '0');
  return `${day} at ${hh}:${mm}`;
}

/**
 * The moment the study's dictionary last CHANGED, which is what makes a
 * document out of date — not the moment an adoption ran (2026-09-17). An
 * adoption that applied no rows, added no question and moved nothing leaves
 * `contentChangedAt` where it was, so nothing is marked stale. Adoptions
 * recorded before this field existed only have `adoptedAt`, and keep their
 * old behaviour.
 */
export function dictionaryChangedSince(
  marker: { adoptedAt?: unknown; contentChangedAt?: unknown } | null | undefined,
): unknown {
  if (!marker) return undefined;
  return marker.contentChangedAt ?? marker.adoptedAt ?? undefined;
}

export function dictionaryChangedNotice(
  adoptedAt: unknown,
  doc: GeneratedDocStamps | null | undefined,
  options?: DictionaryChangedNoticeOptions,
): DictionaryChangedNotice | null {
  const adoptedMs = toMillis(adoptedAt);
  if (adoptedMs === null || !doc) return null;

  const fmt = formatterFor(options?.timeZone);
  const adopted = zonedParts(adoptedMs, fmt);
  const stamps = [toMillis(doc.generatedAt), toMillis(doc.lastModifiedAt)].filter(
    (ms): ms is number => ms !== null,
  );

  const pointer = 'The notice below names the renamed fields and your choices.';
  if (stamps.length === 0) {
    return {
      kind: 'unknown',
      text: `The data dictionary changed on ${describe(adopted, false)}; this document's generation date is not recorded. ${options?.renameNoticeOpen ? pointer : 'Regenerate it to be sure.'}`,
    };
  }

  const effectiveMs = Math.max(...stamps);
  if (effectiveMs >= adoptedMs) return null;
  const effective = zonedParts(effectiveMs, fmt);
  const withTime = sameDay(adopted, effective);
  return {
    kind: 'stale',
    text: `The data dictionary changed on ${describe(adopted, withTime)}; this was generated on ${describe(effective, withTime)}. ${options?.renameNoticeOpen ? pointer : 'Regenerate it.'}`,
  };
}
